import React, { useEffect } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import {
  ChipInput,
  CustomSelect,
  UnifiedDateTimePicker,
  TextArea,
  TextInput,
} from "../../components";
import {
  addAgencies,
  updateAgencies,
  getListOrganization,
} from "@/store/constants/constants.action";
import { connect, ConnectedProps } from "react-redux";
import { RootState } from "../../store";
import { toast } from "../../components";
import { agencyCategoryOptions } from "@/utils/constants/utils";
import SidebarNavigation, {
  SidebarMenuItem,
} from "@/components/SidebarNavigation/SidebarNavigation";
import { User } from "lucide-react";
import { useRef, useState } from "react";
import {
  updateIsFilter,
  updateIsClearFilter,
} from "@/store/media/media.action";
import { scrollToFirstError } from "../../utils";
import { COUNTRIES } from "@/utils/constants/uiConstants";
import { formatEntityName } from "@/utils/utilFunctions/entityName";
import { getBandSizeOfAxis } from "recharts/types/util/ChartUtils";

interface AddAgencyFormProps {
  onClose: () => void;
  agency?: any;
  onFormStateChange?: (state: { isDirty: boolean; isValid: boolean }) => void;
}

export const AddAgencyForm = ({
  onClose,
  addAgencies,
  agency,
  updateAgencies,
  bunchProductList,

  onFormStateChange,
  updateIsFilter,
  updateIsClearFilter,
}: Props) => {
  const [activeSection, setActiveSection] = useState<string>("basic-info");

  // Refs for sections
  const basicInfoRef = useRef<HTMLDivElement>(null);
  const restrictionRef = useRef<HTMLDivElement>(null);
  const teamsRef = useRef<HTMLDivElement>(null);
  // Define sidebar menu items
  const sidebarMenuItems: SidebarMenuItem[] = [
    {
      id: "basic-info",
      label: "Basic Information",
      icon: User,
      sectionRef: basicInfoRef,
    },
    {
      id: "restriction",
      label: "Restriction",
      icon: User,
      sectionRef: restrictionRef,
    },
    {
      id: "teams",
      label: "Teams",
      icon: User,
      sectionRef: teamsRef,
    },
  ];

  const handleApiError = (error: string) => {
    if (typeof error === "string") {
      const fieldErrors: Record<string, string> = {};
      if (
        error?.includes("unique constraint failed") &&
        error.toLowerCase().includes("name")
      ) {
        toast.fail("Agency with this name already exists");
      } else if (
        error?.includes("unique constraint failed") &&
        error.toLowerCase().includes("code")
      ) {
        toast.fail("Agency with this code already exists");
      }
      if (error.toLowerCase().includes("name")) {
        fieldErrors.name = error;
      } else if (error.toLowerCase().includes("code")) {
        fieldErrors.code = error;
      }
      if (Object.keys(fieldErrors).length > 0) {
        Object.entries(fieldErrors).forEach(([field, message]) => {
          formik.setFieldError(field, message);
          formik.setFieldTouched(field, true);
        });
      } else {
        toast.fail("");
      }
    }
  };

  const formik = useFormik({
    initialValues: {
      name: agency?.name || "",
      display_name: agency?.display_name || "",
      description: agency?.description || "",
      status: agency?.status || 1,
      code: agency?.code || "",
      agency_group: agency?.agency_group || "",
      settings: {
        gct: agency?.settings?.gct || 0,
        gcl: agency?.settings?.gcl || [],
        gdt: agency?.settings?.gdt || 0,
        gdl: agency?.settings?.gdl || [],
        redistribution: agency?.settings?.redistribution || 2,
        expiry: agency?.settings?.expiry || null,
        contract_start: agency?.settings?.contract_start || null,
        retention: agency?.settings?.retention || 0,
        archival: agency?.settings?.archival || 0,
        category: agency?.settings?.category || 1,
        teams_allowed: agency?.settings?.teams_allowed || [],
      },
    },
    validationSchema: Yup.object({
      name: Yup.string()
        .required("Agency Name is required")
        .min(3, "Agency Name must be at least 3 characters")
        .max(50, "Agency Name must be at most 50 characters"),
      code: Yup.string()
        .required("Agency Code is required")
        .min(3, "Agency Code must be at least 3 characters")
        .max(50, "Agency Code must be at most 50 characters"),
      settings: Yup.object({
        expiry: Yup.string().required("Contract expiry date is required"),
        contract_start: Yup.string().required(
          "Contract start date is required"
        ),
        category: Yup.number().required("Agency Category is required"),
        teams_allowed: Yup.array()
          .min(1, "At least one team is required")
          .required("Teams allowed are required"),
      }),
      // agency_group: Yup.string().required('Agency Group is required'),
    }),
    onSubmit: async (values) => {
      try {
        updateIsClearFilter?.(true);
        updateIsFilter?.(false);
        if (agency) {
          const params = { ...values, id: agency.id };

          await updateAgencies(
            params,
            () => {
              onClose();
            },
            handleApiError
          );
        } else {
          addAgencies(
            values,
            () => {
              onClose();
            },
            handleApiError
          );
        }
      } catch (error) {
        handleApiError(String(error));
      }
    },
  });

  const handleChange = (values: string[]) => {
    const newApprovers = values.map((value) => {
      const existingApprover = formik.values.settings.teams_allowed.find(
        (approver: string) => approver.startsWith(value)
      );
      return existingApprover ? existingApprover : value;
    });
    formik.setFieldValue("settings.teams_allowed", newApprovers);
  };

  useEffect(() => {
    getListOrganization({});
  }, []);

  useEffect(() => {
    onFormStateChange?.({
      isDirty: formik.dirty,
      isValid: formik.isValid,
    });
  }, [formik.dirty, formik.isValid, onFormStateChange]);

  const productListOptions = bunchProductList.map((item: any) => {
    return {
      label: formatEntityName(item),
      value: item.id,
    };
  });

  const handleSaveForm = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    await formik.setTouched({
      name: true,
      code: true,
      settings: {
        expiry: true,
        contract_start: true,
        teams_allowed: true,
      },
    });
    const errors = await formik.validateForm();
    if (Object.keys(errors).length > 0) {
      scrollToFirstError(errors);
      return;
    }
    await formik.handleSubmit();
  };

  useEffect(() => {
    const handleSave = (e: any) => {
      e.preventDefault();
      handleSaveForm(e);
    };
    window.addEventListener("submit", handleSave);
    return () => {
      window.removeEventListener("submit", handleSave);
    };
  }, []);
  return (
    <div className="mx-2 my-3 row">
      <SidebarNavigation
        menuItems={sidebarMenuItems}
        activeItem={activeSection}
        onItemClick={setActiveSection}
        className="col-sm-5 col-md-4 col-lg-3"
      />
      <form
        className="col-sm-7 col-md-8 col-lg-9 gap-3"
        id="agencyForm"
        onSubmit={async (e) => {
          e.preventDefault();
          await formik.setTouched({
            name: true,
            code: true,
            settings: {
              expiry: true,
              contract_start: true,
              teams_allowed: true,
            },
          });
          const errors = await formik.validateForm();
          if (Object.keys(errors).length > 0) {
            return;
          }
          await formik.handleSubmit();
        }}
      >
        <div ref={basicInfoRef} className="card mx-0 mb-4 pb-3">
          <div className="card-header py-3 bg-light border-0">
            <h6 className="text-uppercase">Basic Information</h6>
          </div>
          <div className="row p-2 gy-2 m-0">
            <div className="col-12">
              <TextInput
                name="name"
                type="text"
                label="Agency Name"
                value={formik.values.name}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                showRequired={true}
                placeholder="Enter Agency Name"
                error={formik.errors.name as string}
                touched={formik.touched.name as boolean}
                autoFocus={!agency}
              />
            </div>
            <div className="col-12">
              <TextInput
                name="display_name"
                type="text"
                label="Display Name"
                value={formik.values.display_name}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                showRequired={false}
                placeholder="Enter Display Name"
              />
            </div>
            <div className="col-12">
              <TextInput
                name="code"
                disabled={agency}
                label="Code"
                type="text"
                value={formik.values.code}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                showRequired={true}
                error={formik.errors.code as string}
                touched={formik.touched.code as boolean}
              />
            </div>
            <div className="col-md-6">
              <TextInput
                name="agency_group"
                label="Agency Group"
                type="text"
                value={formik.values.agency_group}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                showRequired={false}
                // disabled={agency}
              />
            </div>
            <div className="col-md-6">
              <CustomSelect
                value={formik.values.settings.category}
                label="Agency Category"
                name="settings.category"
                options={agencyCategoryOptions}
                disabled={agency}
                showRequired={true}
                placeholder="Agency Category"
                handleChange={(newValue: any) => {
                  formik.setFieldValue("settings.category", newValue.value);
                }}
              />{" "}
            </div>
            <div className="col-12">
              <TextArea
                name="description"
                placeholder="Description here..."
                value={formik.values.description}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                label="Description"
                characterLimit={5000}
                showRequired={false}
              />
            </div>
          </div>
        </div>
        <div ref={restrictionRef} className="card mx-0 mb-4 pb-3">
          <div className="card-header py-3 bg-light border-0">
            <h6 className="text-uppercase">Restriction</h6>
          </div>
          <div className="row p-2 gy-2 m-0">
            <h6 className="fw-semibold">Geo</h6>
            <div className="col-sm-12 col-md-6">
              <CustomSelect
                value={formik.values.settings.gct}
                label="Mode"
                name="settings.gct"
                options={[
                  { label: "Allowed Countries", value: 1 },
                  { label: "Blocked Countries", value: 2 },
                ]}
                disabled={false}
                placeholder="Mode"
                handleChange={(newValue: any) => {
                  formik.setFieldValue("settings.gct", newValue.value);
                }}
              />
            </div>
            <div className="col-sm-12 col-md-6">
              <CustomSelect
                label="Countries"
                options={COUNTRIES}
                disabled={false}
                placeholder="Countries"
                handleChange={(newValue: any) => {
                  const newCountries = [
                    ...new Set(newValue.map((v: any) => v.value)),
                  ];
                  formik.setFieldValue("settings.gcl", newCountries);
                }}
                value={formik.values.settings.gcl}
                multiSelect={true}
                handleClear={() => formik.setFieldValue("settings.gcl", [])}
                updatedOptions={COUNTRIES}
              />
            </div>
          </div>
          <div className="row p-2 gy-2 m-0">
            <h6 className="fw-semibold">Domain</h6>
            <div className="col-sm-12 col-md-6">
              <CustomSelect
                value={formik.values.settings.gdt}
                name="settings.gdt"
                label="Mode"
                options={[
                  { label: "Allowed", value: 1 },
                  { label: "Not Allowed", value: 0 },
                ]}
                disabled={false}
                placeholder="Select Domain Permissions"
                handleChange={(newValue: any) => {
                  formik.setFieldValue("settings.gdt", newValue.value);
                }}
              />
            </div>

            <div className="col col-sm-12 col-md-6">
              <ChipInput
                className="mb-0 mt-0"
                label="Domain"
                value={formik.values.settings.gdl}
                placeholder="Type here to add Domain"
                handleChipChange={(chip: string | string[], action: string) => {
                  const currentValues = formik.values.settings.gdl || [];
                  if (action === "add") {
                    const chipsToAdd = typeof chip === "string" ? [chip] : chip;
                    formik.setFieldValue("settings.gdl", [
                      ...new Set([...currentValues, ...chipsToAdd]),
                    ]);
                  } else if (action === "remove") {
                    const chipsToRemove =
                      typeof chip === "string" ? [chip] : chip;
                    formik.setFieldValue(
                      "settings.gdl",
                      currentValues.filter(
                        (t: string) => !chipsToRemove.includes(t)
                      )
                    );
                  } else if (action === "clear") {
                    formik.setFieldValue("settings.gdl", []);
                  }
                }}
              />
            </div>
          </div>
        </div>
        <div ref={teamsRef} className="card mx-0 mb-4 pb-3">
          <div className="card-header py-3 bg-light border-0">
            <h6 className="text-uppercase">Teams</h6>
          </div>
          <div className="row p-2 gy-2 m-0">
            <div className="col-sm-12 col-md-6">
              <CustomSelect
                value={formik.values.settings.teams_allowed}
                label="Teams allowed"
                name="settings.teams_allowed"
                options={[
                  { label: "All", value: "all" },
                  ...productListOptions,
                ]}
                multiSelect={true}
                disabled={false}
                placeholder="Select Teams"
                handleChange={(newValue: any) => {
                  handleChange(newValue.map((option: any) => option.value));
                }}
                showRequired={true}
                error={formik.errors.settings?.teams_allowed as string}
                touched={formik.touched.settings?.teams_allowed as boolean}
                updatedOptions={productListOptions}
              />
            </div>
            <div className="col-sm-12 col-md-6">
              <CustomSelect
                value={formik.values.settings.redistribution}
                label="Redistribution rights"
                name="settings.redistribution"
                options={[
                  { label: "Yes", value: 1 },
                  { label: "No", value: 0 },
                  { label: "Unknown", value: 2 },
                ]}
                disabled={false}
                placeholder="Select Redistribution Right"
                handleChange={(newValue: any) =>
                  formik.setFieldValue(
                    "settings.redistribution",
                    newValue.value
                  )
                }
              />
            </div>
            <div className="col-sm-12 col-md-6">
              <UnifiedDateTimePicker
                mode="date"
                label="Contract Start Date"
                name="settings.contract_start"
                onBlur={formik.handleBlur}
                onChange={(date: Date | null) => {
                  formik.setFieldValue(
                    "settings.contract_start",
                    date ? date.getTime() : null
                  );
                }}
                value={
                  formik.values.settings.contract_start
                    ? formik.values.settings.contract_start
                    : null
                }
                showRequired={true}
                error={formik.errors.settings?.contract_start as string}
                touched={formik.touched.settings?.contract_start as boolean}
              />
            </div>
            <div className="col-sm-12 col-md-6">
              <UnifiedDateTimePicker
                mode="date"
                label="Contract Expiry Date"
                name="settings.expiry"
                onBlur={formik.handleBlur}
                onChange={(date: Date | null) => {
                  formik.setFieldValue(
                    "settings.expiry",
                    date ? date.getTime() : null
                  );
                }}
                value={
                  formik.values.settings.expiry
                    ? formik.values.settings.expiry
                    : null
                }
                showRequired={true}
                error={formik.errors.settings?.expiry as string}
                touched={formik.touched.settings?.expiry as boolean}
                isContractEndDate={true}
                contractStartDate={
                  formik.values.settings.contract_start
                    ? new Date(formik.values.settings.contract_start)
                    : undefined
                }
              />
            </div>
            <div className="col-sm-12 col-md-6">
              <CustomSelect
                value={formik.values.settings.retention}
                label="Retention policy"
                name="settings.retention"
                options={[
                  { label: "Lifetime", value: 1 },
                  { label: "Till Contract Date", value: 0 },
                ]}
                disabled={false}
                placeholder="Select Retention Policy"
                handleChange={(newValue: any) =>
                  formik.setFieldValue("settings.retention", newValue.value)
                }
              />
            </div>
            <div className="col-sm-12 col-md-6">
              <CustomSelect
                value={formik.values.settings.archival}
                label="Archival policy"
                name="settings.archival"
                options={[
                  { label: "Archival Policy 1", value: 0 },
                  { label: "Archival Policy 2", value: 1 },
                ]}
                disabled={false}
                placeholder="Select Archival Policy"
                handleChange={(newValue: any) =>
                  formik.setFieldValue("settings.archival", newValue.value)
                }
              />
            </div>
            <div className="col-sm-12 col-md-6 H">
              <CustomSelect
                value={formik.values.status}
                label="Status"
                name="status"
                options={[
                  { label: "Active", value: 1 },
                  { label: "Inactive", value: 2 },
                ]}
                disabled={false}
                placeholder="Select Status"
                handleChange={(newValue: any) => {
                  formik.setFieldValue("status", newValue.value);
                }}
              />
            </div>
          </div>
        </div>
      </form>
    </div>
  );
};

const mapState = (state: RootState) => ({
  bunchProductList: state.constants.bunchProductList.list,
});

const mapDispatch = {
  addAgencies,
  updateAgencies,
  updateIsFilter,
  updateIsClearFilter,
};

const connector = connect(mapState, mapDispatch);
type Props = ConnectedProps<typeof connector> & AddAgencyFormProps;

export default connector(AddAgencyForm);
